import { Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "../auth/AuthProvider";
import { LoginPage } from "../auth/LoginPage";
import { AppHeader } from "../components/AppHeader";
import {
  WorkspaceProvider,
  useWorkspace,
} from "../workspace/WorkspaceProvider";

const HelpPage = lazy(() =>
  import("../help/HelpPage").then((module) => ({ default: module.HelpPage })),
);
const SettingsPage = lazy(() =>
  import("../settings/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const CatalystConsolePage = lazy(() =>
  import("../catalyst/CatalystConsolePage").then((module) => ({
    default: module.CatalystConsolePage,
  })),
);
const LegacyNpcForgePage = lazy(() =>
  import("../catalyst/LegacyNpcForgePage").then((module) => ({
    default: module.LegacyNpcForgePage,
  })),
);

function ProtectedApp() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="loading-screen">
        <span className="spinner" />
        Resolving staff session…
      </div>
    );
  if (!user) return <LoginPage />;
  return (
    <WorkspaceProvider>
      <WorkspaceGate />
    </WorkspaceProvider>
  );
}

function WorkspaceGate() {
  const { loading, error } = useWorkspace();
  if (loading)
    return (
      <div className="loading-screen">
        <span className="spinner" />
        Loading workspace…
      </div>
    );
  if (error)
    return (
      <main className="fatal-state">
        <h1>Workspace unavailable</h1>
        <p>{error}</p>
        <p>Your account must be active and assigned to a workspace.</p>
      </main>
    );
  return (
    <div className="app-shell">
      <AppHeader />
      <Suspense
        fallback={
          <div className="loading-screen compact">
            <span className="spinner" />
            Loading tool…
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<CatalystConsolePage />} />
          <Route path="/catalyst/:section" element={<CatalystConsolePage />} />
          <Route path="/legacy" element={<LegacyNpcForgePage />} />
          <Route
            path="/legacy/npcs"
            element={<Navigate to="/legacy" replace />}
          />
          <Route
            path="/editor/:npcId"
            element={<Navigate to="/legacy" replace />}
          />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <ProtectedApp />
    </AuthProvider>
  );
}
